import {createContext, ReactNode, useContext, useMemo} from 'react';
import {} from 'react';
import {
  AssigmentSource,
  ConditionId,
  ScoringSystemsFirstResult,
  BodySiteId,
  PathologyId,
  UserType,
  BaseUser,
  UserId,
  Perspective
} from '@legit-health/ui';
import Patient from '@/patient/interfaces/Patient';
import useLoggedUserContext from '@/shared/contexts/UserContext/useUserContext';

const CurrentPatientContext = createContext<Patient | null>(null);

export function CurrentPatientContextProvider({children}: {children: ReactNode}) {
  const {user} = useLoggedUserContext();
  if (!user || !isPatient(user)) {
    throw new Error('The current user is not a patient');
  }

  const patient = useMemo(
    () =>
      new Patient(
        user.id,
        user.name,
        user.surname,
        user.secondSurname,
        user.gender,
        user.locale,
        user.fullname,
        user.country,
        user.email,
        user.image,
        user.imageThumbnail,
        user.is2faEnabled,
        user.hasCompletedTutorial,
        user.enableExperimentalFeatures,
        user.lastLogin,
        user.allowsReceiveEmails,
        user.type,
        user.birthDate,
        user.telephone,
        user.emergencyContact,
        user.address,
        user.languages,
        user.careGaps,
        user.socialDeterminants,
        user.skinType,
        user.riskFactors,
        user.allergies,
        user.weight,
        user.height,
        user.nhc,
        user.idCard,
        user.healthCenter,
        user.healthCardNumber,
        user.privateInsuranceProvider,
        user.habits,
        user.practitioner,
        user.referredToPractitioners,
        user.canViewResultsOfDiagnosticReports,
        user.isAllowedToUploadPhotos,
        user.assignedPathologies,
        user.isMobileOnlyPhotoEnabled
      ),
    [user]
  );
  return (
    <CurrentPatientContext.Provider value={patient}>{children}</CurrentPatientContext.Provider>
  );
}

export function useCurrentPatient() {
  const patient = useContext(CurrentPatientContext);
  if (!patient) {
    throw new Error('Patient context not set');
  }
  return patient;
}

function isPatient(user: BaseUser): user is PatientApiResponse {
  return (user as Patient).type === UserType.Patient;
}

interface PatientApiResponse extends Omit<Patient, 'conditions'> {
  conditions: ConditionApiResponse[];
}

interface ConditionApiResponse {
  id: ConditionId;
  isTracked: boolean;
  firstImage: string | null;
  firstImageThumbnail: string | null;
  frequency: number;
  assigmentSource: AssigmentSource;
  scoringSystemsFirstResults: ScoringSystemsFirstResult[];
  createdAt: string;
  bodySite: BodySiteId;
  pathology: PathologyId;
  practitioner: UserId;
  firstImageOfPerspective: Record<Perspective, string>;
  canBeTracked: boolean;
}
